import React, { useState } from "react";
import { analyzeAthlete, generateWeeklyReport } from "../../lib/aiAnalyst";

// ─── Markdown renderer ──────────────────────────────────────────────────────
function MD({ text }) {
  const lines = text.split("\n");
  return (
    <div style={{ lineHeight: 1.75 }}>
      {lines.map((line, i) => {
        if (line.startsWith("## ")) {
          return (
            <div key={i} style={{
              fontSize: 11, fontWeight: 700, letterSpacing: "0.1em",
              textTransform: "uppercase", color: "#e85d04",
              margin: "1.4rem 0 0.5rem", paddingBottom: 4,
              borderBottom: "1px solid rgba(232,93,4,0.2)",
            }}>
              {line.replace("## ", "")}
            </div>
          );
        }
        if (line.match(/^\*\*(.+)\*\*$/)) {
          return (
            <div key={i} style={{ fontSize: 13, fontWeight: 600, color: "#fff", margin: "0.6rem 0 0.1rem" }}>
              {line.replace(/\*\*/g, "")}
            </div>
          );
        }
        if (line.startsWith("- ")) {
          const content = line.replace(/^- /, "").replace(/\*\*(.*?)\*\*/g, "$1");
          return (
            <div key={i} style={{ display: "flex", gap: 8, margin: "0.25rem 0", alignItems: "flex-start" }}>
              <span style={{ color: "#e85d04", fontSize: 12, marginTop: 3, flexShrink: 0 }}>▸</span>
              <span style={{ fontSize: 13, color: "#c8c8d0", lineHeight: 1.65 }}>{content}</span>
            </div>
          );
        }
        if (line.trim() === "") return <div key={i} style={{ height: 6 }} />;
        return (
          <p key={i} style={{ fontSize: 13, color: "#c8c8d0", margin: "0.2rem 0", lineHeight: 1.7 }}>
            {line.replace(/\*\*(.*?)\*\*/g, "$1")}
          </p>
        );
      })}
    </div>
  );
}

// ─── Pulsing bot icon ───────────────────────────────────────────────────────
function BotIcon({ size = 20, pulse = false }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      style={pulse ? { animation: "rollbot-pulse 1.5s ease-in-out infinite" } : {}}>
      <style>{`@keyframes rollbot-pulse{0%,100%{opacity:1}50%{opacity:0.4}}`}</style>
      <rect x="3" y="8" width="18" height="13" rx="3" fill="none" stroke="#e85d04" strokeWidth="1.5"/>
      <circle cx="9" cy="14" r="2" fill="#e85d04"/>
      <circle cx="15" cy="14" r="2" fill="#e85d04"/>
      <path d="M12 3v5M9 3h6" stroke="#e85d04" strokeWidth="1.5" strokeLinecap="round"/>
      <path d="M7 21v2M17 21v2" stroke="#e85d04" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  );
}

// ─── Dot loader ─────────────────────────────────────────────────────────────
function Dots() {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 5, padding: "6px 0" }}>
      {[0, 1, 2].map(i => (
        <div key={i} style={{
          width: 5, height: 5, borderRadius: "50%", background: "#e85d04",
          animation: `rollbot-dot 1.2s ease-in-out ${i * 0.2}s infinite`,
        }} />
      ))}
      <style>{`@keyframes rollbot-dot{0%,80%,100%{transform:scale(0.7);opacity:0.4}40%{transform:scale(1);opacity:1}}`}</style>
    </div>
  );
}

// ─── 6 Metric Cards ─────────────────────────────────────────────────────────
function MetricCards({ metrics, role }) {
  if (!metrics) return null;
  const { cardio, honesty, fitness, overtraining, hillQuality, teamPos } = metrics;

  // Hanya tampilkan card yang punya data
  const cards = [
    cardio && {
      icon: "❤️",
      label: "Efisiensi Kardio",
      value: parseFloat(cardio.delta) > 0
        ? `+${cardio.delta}% membaik`
        : `${cardio.delta}% menurun`,
      sub: "HR vs Speed ratio",
      signal: parseFloat(cardio.delta) > 0 ? "good" : "warn",
    },
    honesty && {
      icon: "🎯",
      label: "Kejujuran Effort",
      value: Math.abs(parseFloat(honesty.avgDiff)) < 10
        ? "Akurat"
        : honesty.avgDiff > 0
        ? "Under-report RPE"
        : "Over-report RPE",
      sub: `${honesty.outlierCount} sesi anomali`,
      signal: honesty.outlierCount > 2 ? "warn" : "good",
    },
    fitness && {
      icon: "📈",
      label: "Tren Kebugaran",
      value: `${parseFloat(fitness.delta) >= 0 ? "+" : ""}${fitness.delta} km/j`,
      sub: `${fitness.olderAvgSpeed} → ${fitness.recentAvgSpeed} km/j`,
      signal: parseFloat(fitness.delta) >= 0 ? "good" : "warn",
    },
    overtraining && {
      icon: "⚡",
      label: "Risiko Overtraining",
      value: overtraining.risk === "tinggi" ? "⚠ Tinggi" : overtraining.risk === "sedang" ? "Sedang" : "Rendah",
      sub: `HR trend ${overtraining.hrTrend > 0 ? "+" : ""}${overtraining.hrTrend} bpm`,
      signal: overtraining.risk === "tinggi" ? "danger" : overtraining.risk === "sedang" ? "warn" : "good",
    },
    hillQuality && {
      icon: "⛰",
      label: "Hill Quality",
      value: `${hillQuality.avgDegradation}% degradasi`,
      sub: parseFloat(hillQuality.avgDegradation) > 15 ? "Terlalu tajam" : "Wajar",
      signal: parseFloat(hillQuality.avgDegradation) > 15 ? "warn" : "good",
    },
    teamPos && {
      icon: "👥",
      label: "Posisi di Tim",
      value: `${parseFloat(teamPos.speedPct) >= 0 ? "+" : ""}${teamPos.speedPct}% vs rata-rata`,
      sub: `Kategori ${teamPos.category}`,
      signal: parseFloat(teamPos.speedPct) >= 0 ? "good" : "warn",
    },
  ].filter(Boolean);

  if (cards.length === 0) return null;

  // Parent: sembunyikan label teknis
  const parentLabels = {
    "Efisiensi Kardio": "Kebugaran Jantung",
    "Kejujuran Effort": "Konsistensi Usaha",
    "Tren Kebugaran": "Perkembangan Kecepatan",
    "Risiko Overtraining": "Tanda Kelelahan",
    "Hill Quality": "Kualitas Tanjakan",
    "Posisi di Tim": "Posisi di Tim",
  };

  const signalColor = { good: "#1D9E75", warn: "#e8a004", danger: "#e85d04" };

  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{
        fontSize: 10, color: "#555", letterSpacing: "0.1em",
        textTransform: "uppercase", marginBottom: 8,
      }}>
        6 Metrik Analisis
      </div>
      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(3, 1fr)",
        gap: 6,
      }}>
        {cards.map((c, i) => (
          <div key={i} style={{
            background: "#1a1d2e",
            border: `1px solid ${signalColor[c.signal]}33`,
            borderRadius: 8,
            padding: "10px 10px 8px",
          }}>
            <div style={{ fontSize: 16, marginBottom: 4 }}>{c.icon}</div>
            <div style={{
              fontSize: 9, color: "#555", letterSpacing: "0.06em",
              textTransform: "uppercase", marginBottom: 3,
            }}>
              {role === "parent" ? (parentLabels[c.label] || c.label) : c.label}
            </div>
            <div style={{
              fontSize: 12, fontWeight: 700,
              color: signalColor[c.signal],
              lineHeight: 1.3,
            }}>
              {c.value}
            </div>
            {role !== "parent" && (
              <div style={{ fontSize: 10, color: "#444", marginTop: 2 }}>{c.sub}</div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Main Component ──────────────────────────────────────────────────────────
export default function RollBOT({
  athlete,
  sessions = [],
  coachNotes = [],
  hillLogs = [],
  badges = [],
  teamAverages = null,
  role = "coach",
}) {
  const [tab, setTab] = useState("analyze");
  const [analysis, setAnalysis] = useState(null); // { text, metrics }
  const [analyzing, setAnalyzing] = useState(false);
  const [analyzeErr, setAnalyzeErr] = useState(null);
  const [weekly, setWeekly] = useState(null);
  const [weeklyLoading, setWeeklyLoading] = useState(false);
  const [weeklyErr, setWeeklyErr] = useState(null);

  const name = athlete?.full_name || athlete?.name || "Atlet";

  // ── Analisa Performa ────────────────────────────────────────────────────────
  const handleAnalyze = async () => {
    setAnalyzing(true);
    setAnalyzeErr(null);
    setAnalysis(null);
    try {
      const result = await analyzeAthlete({
        athlete,
        sessions,
        coachNotes,
        hillLogs,
        badges,
        teamAverages,
        role,
      });
      setAnalysis(result); // { text, metrics }
    } catch (e) {
      setAnalyzeErr(e.message || "Gagal menganalisa.");
    } finally {
      setAnalyzing(false);
    }
  };

  // ── Laporan Mingguan ────────────────────────────────────────────────────────
  const handleWeekly = async () => {
    setWeeklyLoading(true);
    setWeeklyErr(null);
    setWeekly(null);
    try {
      const result = await generateWeeklyReport({ athlete, sessions, coachNotes, role });
      setWeekly(result);
    } catch (e) {
      setWeeklyErr(e.message || "Gagal membuat laporan.");
    } finally {
      setWeeklyLoading(false);
    }
  };

  // ── Shared styles ───────────────────────────────────────────────────────────
  const tabBtn = (t) => ({
    fontSize: 12, fontWeight: tab === t ? 600 : 400,
    color: tab === t ? "#e85d04" : "#666",
    background: tab === t ? "rgba(232,93,4,0.1)" : "transparent",
    border: tab === t ? "1px solid rgba(232,93,4,0.3)" : "1px solid transparent",
    borderRadius: 6, padding: "5px 12px", cursor: "pointer",
    transition: "all 0.15s ease", letterSpacing: "0.02em",
  });

  const actionBtn = (disabled) => ({
    display: "flex", alignItems: "center", gap: 8, justifyContent: "center",
    background: disabled ? "#1e1e28" : "linear-gradient(135deg, #e85d04, #c44b00)",
    color: disabled ? "#555" : "#fff",
    border: "none", borderRadius: 8,
    padding: "10px 24px", fontSize: 13, fontWeight: 600,
    cursor: disabled ? "default" : "pointer",
    letterSpacing: "0.03em",
    boxShadow: disabled ? "none" : "0 4px 16px rgba(232,93,4,0.35)",
    transition: "all 0.2s ease",
  });

  const rerunBtn = {
    fontSize: 11, color: "#555",
    background: "transparent", border: "1px solid #2a2d3e",
    borderRadius: 6, padding: "4px 10px", cursor: "pointer",
    letterSpacing: "0.03em",
  };

  const errBox = (msg) => (
    <div style={{
      background: "rgba(220,50,50,0.08)", border: "1px solid rgba(220,50,50,0.25)",
      borderRadius: 8, padding: "10px 14px", fontSize: 12, color: "#f08080", marginTop: 12,
    }}>
      ⚠ {msg}
    </div>
  );

  const thisWeekCount = sessions.filter(s => {
    const d = s.session_date && new Date(s.session_date);
    return d && d >= new Date(Date.now() - 7 * 24 * 3600 * 1000);
  }).length;

  return (
    <div style={{
      background: "#13141f",
      border: "1px solid #2a2d3e",
      borderRadius: 14,
      overflow: "hidden",
      fontFamily: "'Helvetica Neue', Helvetica, sans-serif",
    }}>

      {/* ── Header ── */}
      <div style={{
        background: "linear-gradient(135deg, #1a1d2e 60%, #1e1424)",
        borderBottom: "1px solid #2a2d3e",
        padding: "14px 18px",
        display: "flex", alignItems: "center", justifyContent: "space-between",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{
            width: 36, height: 36, borderRadius: "50%",
            background: "rgba(232,93,4,0.12)",
            border: "1px solid rgba(232,93,4,0.3)",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <BotIcon size={18} />
          </div>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: "#fff", letterSpacing: "0.04em" }}>
              Roll<span style={{ color: "#e85d04" }}>BOT</span>
            </div>
            <div style={{ fontSize: 10, color: "#555", letterSpacing: "0.06em", textTransform: "uppercase" }}>
              {sessions.length} sesi · {name}
            </div>
          </div>
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          <button onClick={() => setTab("analyze")} style={tabBtn("analyze")}>Analisa</button>
          <button onClick={() => setTab("weekly")} style={tabBtn("weekly")}>Laporan Mingguan</button>
        </div>
      </div>

      {/* ── Tab: Analisa Performa ── */}
      {tab === "analyze" && (
        <div style={{ padding: 18 }}>
          {!analysis && !analyzing && (
            <div style={{
              textAlign: "center", padding: "28px 16px",
              background: "rgba(232,93,4,0.03)",
              border: "1px dashed rgba(232,93,4,0.15)",
              borderRadius: 10,
            }}>
              <BotIcon size={36} />
              <div style={{ fontSize: 14, fontWeight: 600, color: "#fff", margin: "12px 0 6px" }}>
                Analisa mendalam {name}
              </div>
              <div style={{ fontSize: 12, color: "#666", marginBottom: 20, lineHeight: 1.7, maxWidth: 340, margin: "0 auto 20px" }}>
                RollBOT membaca {sessions.length} sesi terakhir · menghitung 6 metrik ·{" "}
                {role === "parent" ? "menyajikan dalam bahasa yang mudah dipahami" : "memberikan insight teknis yang tidak terlihat dari angka biasa"}.
              </div>
              <button onClick={handleAnalyze} style={actionBtn(false)}>
                <BotIcon size={14} /> Jalankan Analisa
              </button>
            </div>
          )}

          {analyzing && (
            <div style={{ textAlign: "center", padding: "28px 16px" }}>
              <BotIcon size={28} pulse />
              <div style={{ display: "flex", justifyContent: "center", marginTop: 12 }}><Dots /></div>
              <div style={{ fontSize: 12, color: "#666", marginTop: 6 }}>RollBOT sedang membaca data & menghitung 6 metrik...</div>
            </div>
          )}

          {analyzeErr && errBox(analyzeErr)}

          {analysis && !analyzing && (
            <div>
              {/* 6 Metric Cards — tampil setelah analisis selesai */}
              <MetricCards metrics={analysis.metrics} role={role} />

              <div style={{
                background: "rgba(232,93,4,0.04)",
                border: "1px solid rgba(232,93,4,0.15)",
                borderRadius: 10, padding: "14px 16px",
              }}>
                <MD text={analysis.text} />
              </div>
              <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 10 }}>
                <button onClick={handleAnalyze} style={rerunBtn}>↺ Analisa ulang</button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Tab: Laporan Mingguan ── */}
      {tab === "weekly" && (
        <div style={{ padding: 18 }}>
          {/* Stats strip */}
          <div style={{
            display: "grid", gridTemplateColumns: "repeat(3, 1fr)",
            gap: 8, marginBottom: 16,
          }}>
            {[
              { label: "Sesi minggu ini", value: thisWeekCount },
              { label: "Total sesi", value: sessions.length },
              { label: "Catatan coach", value: coachNotes.length },
            ].map(s => (
              <div key={s.label} style={{
                background: "#1a1d2e", border: "1px solid #2a2d3e",
                borderRadius: 8, padding: "10px 12px", textAlign: "center",
              }}>
                <div style={{ fontSize: 20, fontWeight: 700, color: "#e85d04" }}>{s.value}</div>
                <div style={{ fontSize: 10, color: "#555", marginTop: 2, letterSpacing: "0.04em", textTransform: "uppercase" }}>{s.label}</div>
              </div>
            ))}
          </div>

          {!weekly && !weeklyLoading && (
            <div style={{
              textAlign: "center", padding: "24px 16px",
              background: "rgba(232,93,4,0.03)",
              border: "1px dashed rgba(232,93,4,0.15)",
              borderRadius: 10,
            }}>
              <div style={{ fontSize: 28, marginBottom: 10 }}>📋</div>
              <div style={{ fontSize: 14, fontWeight: 600, color: "#fff", marginBottom: 6 }}>
                Laporan Mingguan
              </div>
              <div style={{ fontSize: 12, color: "#666", marginBottom: 20, lineHeight: 1.7 }}>
                {role === "parent"
                  ? "RollBOT merangkum perkembangan anak Anda minggu ini dalam bahasa yang mudah dipahami."
                  : "RollBOT membandingkan data 7 hari ini vs minggu lalu — lengkap dengan rekomendasi."}
              </div>
              <button onClick={handleWeekly} style={actionBtn(false)}>
                <BotIcon size={14} /> Generate Laporan
              </button>
            </div>
          )}

          {weeklyLoading && (
            <div style={{ textAlign: "center", padding: "28px 16px" }}>
              <BotIcon size={28} pulse />
              <div style={{ display: "flex", justifyContent: "center", marginTop: 12 }}><Dots /></div>
              <div style={{ fontSize: 12, color: "#666", marginTop: 6 }}>Menyusun laporan mingguan...</div>
            </div>
          )}

          {weeklyErr && errBox(weeklyErr)}

          {weekly && !weeklyLoading && (
            <div>
              <div style={{
                fontSize: 10, color: "#555", textTransform: "uppercase",
                letterSpacing: "0.08em", marginBottom: 10,
                display: "flex", alignItems: "center", gap: 6,
              }}>
                <span style={{ width: 20, height: 1, background: "#2a2d3e", display: "inline-block" }} />
                Laporan minggu {new Date().toLocaleDateString("id-ID", { day: "numeric", month: "long", year: "numeric" })}
                <span style={{ flex: 1, height: 1, background: "#2a2d3e", display: "inline-block" }} />
              </div>
              <div style={{
                background: "rgba(232,93,4,0.04)",
                border: "1px solid rgba(232,93,4,0.15)",
                borderRadius: 10, padding: "14px 16px",
              }}>
                <MD text={weekly} />
              </div>
              <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 10 }}>
                <button onClick={handleWeekly} style={rerunBtn}>↺ Generate ulang</button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Footer ── */}
      <div style={{
        borderTop: "1px solid #1e2030",
        padding: "8px 18px",
        display: "flex", alignItems: "center", gap: 6,
      }}>
        <div style={{ width: 5, height: 5, borderRadius: "50%", background: "#1D9E75", flexShrink: 0 }} />
        <span style={{ fontSize: 10, color: "#444", letterSpacing: "0.04em" }}>
          Powered by RollBOT ·{" "}
          {role === "parent" ? "Laporan untuk orang tua" : role === "admin" ? "Analisa admin" : "Analisa coach"}
        </span>
      </div>
    </div>
  );
}
